import { BffFacility, FacilityFieldsFragment } from '@aspendental/shared-components-web/lib/graphql/__generated/sdk';
import { mountStoreDevtool } from 'simple-zustand-devtools';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type TimeSlot = {
	timeSlot: {
		booked: number;
		date: string;
		datetime: string;
		open: number;
		open_slots: any[];
		ptc: any | null;
		time: string;
	};
};

type AppointmentInformationState = {
	chosenCenterData: BffFacility | FacilityFieldsFragment | null;
	chosenAppointmentData: any | null;
	chosenAppointmentKey: string | null;
	shouldPreserveAppointmentAndCenterData: boolean;
	appointmentsToShow: number;
	presetCenterID: string | null; // Used only for tracking purposes
	presetCenterName: string | null; // Used only for tracking purposes
	// Details saved for tracking/data layer
	trackAppointmentTimesShown: number;
	trackAppointmentDaysShown: number;
	noChosenAppointmentError: boolean;
	loadingAppointments: boolean;
};

type AppointmentInformationActions = {
	setChosenCenterData: (centerData: BffFacility | FacilityFieldsFragment | null) => void;
	setChosenAppointmentData: (timeSlot: TimeSlot | unknown) => void;
	setChosenAppointmentKey: (id: string) => void;
	setShouldPreserveAppointmentAndCenterData: (shouldPreserve: boolean) => void;
	setAppointmentsToShow: (appointmentsToShow: number) => void;
	setPresetCenterID: (presetCenterID: string) => void;
	setPresetCenterName: (presetCenterName: string) => void;
	setTrackAppointmentTimesShown: (count: number) => void;
	setTrackAppointmentDaysShown: (count: number) => void;
	setNoChosenAppointmentError: (noChosenAppointmentError: boolean) => void;
	setLoadingAppointments: (loadingAppointments: boolean) => void;
	clear: () => void;
};

const useAppointmentInformationStore = create<AppointmentInformationState & AppointmentInformationActions>()(
	persist(
		(set) => ({
			chosenCenterData: null,
			setChosenCenterData: (chosenCenterData) => set({ chosenCenterData }),
			chosenAppointmentData: null,
			setChosenAppointmentData: (chosenAppointmentData) => set({ chosenAppointmentData }),
			chosenAppointmentKey: null,
			setChosenAppointmentKey: (chosenAppointmentKey) => set({ chosenAppointmentKey }),
			shouldPreserveAppointmentAndCenterData: false,
			setShouldPreserveAppointmentAndCenterData: (shouldPreserve) =>
				set({ shouldPreserveAppointmentAndCenterData: shouldPreserve }),
			appointmentsToShow: 6,
			setAppointmentsToShow: (appointmentsToShow) => set({ appointmentsToShow }),
			presetCenterID: null,
			setPresetCenterID: (presetCenterID) => set({ presetCenterID }),
			presetCenterName: null,
			setPresetCenterName: (presetCenterName) => set({ presetCenterName }),
			trackAppointmentTimesShown: 0,
			setTrackAppointmentTimesShown: (trackAppointmentTimesShown) => set({ trackAppointmentTimesShown }),
			trackAppointmentDaysShown: 0,
			setTrackAppointmentDaysShown: (trackAppointmentDaysShown) => set({ trackAppointmentDaysShown }),
			noChosenAppointmentError: false,
			setNoChosenAppointmentError: (noChosenAppointmentError) => set({ noChosenAppointmentError }),
			loadingAppointments: false,
			setLoadingAppointments: (loadingAppointments) => set({ loadingAppointments }),

			clear: () => {
				set({
					chosenCenterData: null,
					chosenAppointmentData: null,
					chosenAppointmentKey: null,
					shouldPreserveAppointmentAndCenterData: false,
					appointmentsToShow: 6,
					presetCenterID: null,
					presetCenterName: null,
					trackAppointmentTimesShown: 0,
					trackAppointmentDaysShown: 0,
					noChosenAppointmentError: false,
					loadingAppointments: false,
				});

				localStorage.removeItem('appointment-information-store');
			},
		}),
		{
			name: 'appointment-information-store',
			partialize: (state) => ({
				chosenCenterData: state.chosenCenterData,
				chosenAppointmentData: state.chosenAppointmentData,
			}),
		}
	)
);

// In Development Mode, Mount Devtool for Debugging
if (process.env.NODE_ENV === 'development') {
	mountStoreDevtool('useAppointmentInformationStore', useAppointmentInformationStore);
}

export default useAppointmentInformationStore;
